import { Sparkles, Users, Zap, FileCheck } from "lucide-react";

const T = {
  ar: {
    eyebrow: "ما هي حاسبة برولانسر؟",
    title: "حاسبة برولانسر — حاسبة لتسعير خدمات العمل الحر في السعودية",
    description:
      "حاسبة برولانسر (Prolancer Calculator) هي أداة متخصّصة لتسعير خدمات المستقلين والفريلانسر في المملكة العربية السعودية. تحسب سعر أي خدمة بالريال السعودي بناءً على نوع الخدمة، مستوى الخبرة، تعقيد المشروع، الوقت المطلوب، ودرجة الاستعجال — مع اقتراحات ذكاء اصطناعي (Claude) تعطيك سعرًا موصى به مبنيًا على جدول أسعار مرجعي للسوق السعودي ووصف مشروعك.",
    cards: [
      {
        icon: "users",
        title: "لمن هذه الحاسبة؟",
        text: "مطورين ومصممين وكتّاب محتوى، مديري سوشال ميديا، مصممي UI/UX، مونتيري فيديو، ومطوري متاجر إلكترونية — جميع تخصصات العمل الحر في السعودية، للمبتدئ والخبير والاستشاري.",
      },
      {
        icon: "sparkles",
        title: "ماذا تحسب؟",
        text: "تعطيك ثلاث نتائج فورية بالريال السعودي: سعر الفوز بالطلب، السعر الموصى به، وسعر القيمة العالية — بناءً على معادلة تجمع ساعات العمل، معاملات الخبرة والتعقيد، والتعديلات.",
      },
      {
        icon: "file",
        title: "ماذا تقدّم إضافة؟",
        text: "تصدير العرض كملف PDF احترافي، 3 اقتراحات AI مجانًا شهريًا، 8 أدلّة تسعير متخصصة، وأدوات تكامل iframe/API للمنصات والمواقع.",
      },
    ],
  },
  en: {
    eyebrow: "What is Prolancer Calculator?",
    title: "Prolancer Calculator — a pricing tool for Saudi freelancers",
    description:
      "Prolancer Calculator is a dedicated pricing tool for freelancers in Saudi Arabia. It computes the right price in SAR for any service based on service type, experience level, project complexity, time required and urgency — with Claude-powered AI suggestions built on reference Saudi market rates and your project description.",
    cards: [
      {
        icon: "users",
        title: "Who is it for?",
        text: "Developers, designers, content writers, social-media managers, UI/UX designers, video editors and e-commerce builders — every freelance specialty in Saudi Arabia, from beginner to expert consultant.",
      },
      {
        icon: "sparkles",
        title: "What does it compute?",
        text: "Three instant results in SAR: a win-the-bid price, the recommended price, and a high-value price — built from a formula combining hours, experience and complexity factors, and revisions.",
      },
      {
        icon: "file",
        title: "What extras come with it?",
        text: "Pro PDF proposal export, 3 free AI suggestions per month, 8 service pricing guides, and iframe/API integration tools for platforms and websites.",
      },
    ],
  },
};

const ICONS = { users: Users, sparkles: Sparkles, file: FileCheck, zap: Zap };

export default function AboutCalculator({ lang = "ar", theme = "dark" }) {
  const dark = theme === "dark";
  const ar = lang === "ar";
  const L = ar ? T.ar : T.en;
  const arabicFont = ar ? "font-arabic" : "font-outfit";

  return (
    <section
      id="about-calculator"
      data-testid="about-calculator-section"
      dir={ar ? "rtl" : "ltr"}
      className={`${dark ? "bg-[#182138]" : "bg-white"}`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.25em] ${
          dark ? "bg-[#18c98f]/12 border-[#18c98f]/40 text-[#18c98f]" : "bg-[#18c98f]/10 border-[#18c98f]/40 text-[#12a777]"
        } ${arabicFont}`}>
          <Sparkles className="h-3.5 w-3.5" />
          {L.eyebrow}
        </div>
        <h2
          data-testid="about-calc-title"
          className={`mt-4 font-extrabold tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-[1.2] max-w-3xl ${arabicFont} ${dark ? "text-white" : "text-[#182138]"}`}
        >
          {L.title}
        </h2>
        <p className={`mt-4 max-w-3xl text-sm sm:text-base leading-relaxed ${dark ? "text-white/75" : "text-[#2d2c35]/80"} ${arabicFont}`}>
          {L.description}
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {L.cards.map((c, i) => {
            const Icon = ICONS[c.icon];
            return (
              <div
                key={i}
                data-testid={`about-card-${i}`}
                className={`rounded-2xl border p-5 ${
                  dark ? "bg-[#1f2a47] border-[#2e3a5c]" : "bg-[#f3f8ff] border-[#e5e5ea]"
                }`}
              >
                <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[#18c98f]/15 text-[#18c98f]">
                  <Icon className="h-5 w-5" />
                </div>
                <div className={`mt-3 font-bold text-base ${arabicFont} ${dark ? "text-white" : "text-[#182138]"}`}>
                  {c.title}
                </div>
                <div className={`mt-1.5 text-[13px] leading-relaxed ${dark ? "text-white/70" : "text-[#2d2c35]/70"} ${arabicFont}`}>
                  {c.text}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
